import React, { useState } from 'react';
import './style.scss';
import ListWithDetails from 'components/Common/ListWithDetails/index';
import GenericBadge from 'components/Utils/GenericBadge/index';
import ClinicDetails from 'components/Clinic/ClinicDetails';
import MiscUtils from 'services/MiscUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import { ResponsiveMediaCard } from 'services/UIUtils';
import StaffRoles from 'components/Clinic/StaffRoles';
import ExportPatients from 'components/Patient/ExportPatients';
import RouteUtils from 'services/RouteUtils';
import DocumentTemplateList from 'components/HealthRecords/Documents/DocumentTemplateList';
import PerformanceUtils from 'services/PerformanceUtils';
import CorporateClients from 'components/Misc/CorporateClients';
import ShiftsEditor from 'components/Schedule/ShiftsEditor';
import BookingFlowOptions from 'components/Clinic/BookingFlowOptions';
import ConcernRequirementsManager from 'components/Clinic/ConcernRequirementsManager';
import ContactsSearch from 'components/Contacts/ContactsSearch';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import Icon from 'components/Utils/NewIcon';
import { NavStack, usePathParams } from 'components/Navigation/NavStack';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import DefaultModal from 'components/Utils/DefaultModal';
import StaffRegisterPatient from 'components/Patient/StaffRegisterPatient';
import OverdueSubscriptions from 'components/Payments/OverdueSubscriptions';
import { PendingTemplateRequestsCount, IsStripeEnabled } from 'careand-redux/selectors';
import GenericDirectory from 'components/Directories/GenericDirectory';
import StatisticsDashboard from '../StatisticsDashboard';
import PromptDashboard from 'components/Clinic/PromptDashboard';
import AssetsManager from 'components/Clinic/AssetsManager';
import HooksManagement from 'components/Hooks/HooksManagement';
import TenantOptions from 'components/TenantOptions';
import { useFeatureAccess } from 'hooks/useFeatureAccess';

const ClinicsDirectory = () => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const params = usePathParams();

  const clinics = useAppSelector((state) => state.resource.clinics);
  const pendingRequests = useAppSelector((state) => state.shifts.pendingRequests);
  const unpaidCount = useAppSelector((state) => state.corporations.unpaidCount);

  // Use centralized feature access
  const { checkAccess, getFeatureConfig } = useFeatureAccess();
  const stripeEnabled = useAppSelector(IsStripeEnabled);

  const [loadingAdd, setLoadingAdd] = useState(false);
  const [newPatientModal, setNewPatientModal] = useState(false);
  const [addConfirmationDialog, setAddConfirmationDialog] = useState(false);

  const corporateBadge = Object.values(unpaidCount).reduce((acc, val) => acc + val, 0);
  const shiftsBadge = pendingRequests.length;

  const pendingTemplateRequests = useAppSelector(PendingTemplateRequestsCount);

  async function addClinic() {
    setLoadingAdd(true);
    await dispatch(
      api.Resource.setClinicInfo({ clinicInfo: { name: 'Unnamed Clinic', hide: true } }),
      {
        errorTitle: 'Add Clinic',
      },
    );
    setLoadingAdd(false);
    setAddConfirmationDialog(false);
  }

  function goToPage(route, params) {
    route = RouteUtils.getRoute(route, params);
    nav.push(route);
  }

  function onRegisterSuccess(patient) {
    let route = RouteUtils.getRoute(RouteUtils.routes().patient, {
      ':patientId': patient.id,
    });
    nav.push(route);
  }

  let tab = params.tab;
  tab = !!tab ? PerformanceUtils.capitalize(tab.split('-').join(' ')) : undefined;

  const routes = RouteUtils.routes();

  const generateRoutes = () => {
    const routeConfig = {};

    if (checkAccess('statistics')) {
      routeConfig[routes.clinicStatisticsDashboard] = {
        element: <StatisticsDashboard />,
      };
    }

    if (checkAccess('shifts')) {
      routeConfig[routes.shifts] = {
        element: <ShiftsEditor />,
      };
    }

    if (checkAccess('bookingOptions')) {
      routeConfig[routes.bookingOptions] = {
        element: <BookingFlowOptions />,
      };
    }

    if (checkAccess('concernRequirements')) {
      routeConfig[routes.concernRequirements] = {
        element: <ConcernRequirementsManager />,
      };
    }

    if (checkAccess('staffRoles')) {
      routeConfig[routes.staffRoles] = {
        element: <StaffRoles />,
      };
    }

    if (checkAccess('corporate')) {
      routeConfig[routes.corporate] = {
        element: <CorporateClients />,
      };
    }

    if (checkAccess('overdueSubscriptions') && stripeEnabled) {
      routeConfig[routes.overdueSubscriptions] = {
        element: <OverdueSubscriptions />,
      };
    }

    if (checkAccess('templates')) {
      routeConfig[routes.templates] = {
        element: (
          <div className="padded-box full-width bg-white">
            <DocumentTemplateList
              className="template-directory"
              hideTitle={true}
              showSecondaryName={true}
              isOwn={false}
            />
          </div>
        ),
      };
    }

    if (checkAccess('clinicDetails')) {
      routeConfig[routes.clinicDetails] = {
        element: (
          <ListWithDetails
            items={clinics}
            renderBadge={(clinic) => <ClinicBadge key={clinic.id} clinic={clinic} />}
            renderDetails={(clinic) => <ClinicDetails key={clinic.id} clinic={clinic} />}
          />
        ),
      };
    }

    if (checkAccess('promptDashboard')) {
      routeConfig[routes.promptDashboard] = {
        element: <PromptDashboard />,
      };
    }

    if (checkAccess('assetsManager')) {
      routeConfig[routes.assetsManager] = {
        element: <AssetsManager />,
      };
    }

    if (checkAccess('hooksManagement')) {
      routeConfig[routes.hooksManagement] = {
        element: <HooksManagement />,
        authenticated: true,
      };
    }

    if (checkAccess('tenantOptions')) {
      routeConfig[routes.tenantOptions] = {
        element: <TenantOptions />,
        authenticated: true,
      };
    }

    routeConfig[routes.contacts] = {
      element: <ContactsSearch fullscreen={true} />,
    };

    return routeConfig;
  };

  // Generate feature cards using centralized permissions
  const renderFeatureCards = () => {
    const cards = [];

    if (checkAccess('statistics')) {
      const config = getFeatureConfig('statistics');
      cards.push(
        <ResponsiveMediaCard
          key="statistics"
          title={config.title}
          text={config.description}
          onClick={() =>
            goToPage(RouteUtils.routes().clinicStatisticsDashboard, {
              ':section': 'clinic',
            })
          }
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('shifts')) {
      const config = getFeatureConfig('shifts');
      cards.push(
        <ResponsiveMediaCard
          key="shifts"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().shifts)}
          img={config.icon}
          badgeCount={shiftsBadge}
          allowed={true}
        />,
      );
    }

    if (checkAccess('promptDashboard')) {
      const config = getFeatureConfig('promptDashboard');
      cards.push(
        <ResponsiveMediaCard
          key="promptDashboard"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().promptDashboard)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('assetsManager')) {
      const config = getFeatureConfig('assetsManager');
      cards.push(
        <ResponsiveMediaCard
          key="assetsManager"
          title={config.title}
          text={config.description}
          img={config.icon}
          onClick={() => goToPage(RouteUtils.routes().assetsManager)}
          allowed={true}
        />,
      );
    }

    if (checkAccess('templates')) {
      const config = getFeatureConfig('templates');
      cards.push(
        <ResponsiveMediaCard
          key="templates"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().templates)}
          img={config.icon}
          allowed={true}
          badgeCount={pendingTemplateRequests}
        />,
      );
    }

    if (checkAccess('bookingOptions')) {
      const config = getFeatureConfig('bookingOptions');
      cards.push(
        <ResponsiveMediaCard
          key="bookingOptions"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().bookingOptions)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('concernRequirements')) {
      const config = getFeatureConfig('concernRequirements');
      cards.push(
        <ResponsiveMediaCard
          key="concernRequirements"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().concernRequirements)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('staffRoles')) {
      const config = getFeatureConfig('staffRoles');
      cards.push(
        <ResponsiveMediaCard
          key="staffRoles"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().staffRoles)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    cards.push(<ExportPatients key="exportPatients" />);

    if (checkAccess('overdueSubscriptions') && stripeEnabled) {
      const config = getFeatureConfig('overdueSubscriptions');
      cards.push(
        <ResponsiveMediaCard
          key="overdueSubscriptions"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().overdueSubscriptions)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('clinicDetails')) {
      const config = getFeatureConfig('clinicDetails');
      cards.push(
        <ResponsiveMediaCard
          key="clinicDetails"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().clinicDetails)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('corporate')) {
      const config = getFeatureConfig('corporate');
      cards.push(
        <ResponsiveMediaCard
          key="corporate"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().corporate)}
          img={config.icon}
          badgeCount={corporateBadge}
          allowed={true}
        />,
      );
    }

    if (checkAccess('contacts')) {
      const config = getFeatureConfig('contacts');
      cards.push(
        <ResponsiveMediaCard
          key="contacts"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().contacts)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('addPatient')) {
      const config = getFeatureConfig('addPatient');
      cards.push(
        <ResponsiveMediaCard
          key="addPatient"
          title={config.title}
          text={config.description}
          onClick={() => setNewPatientModal(true)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('hooksManagement')) {
      const config = getFeatureConfig('hooksManagement');
      cards.push(
        <ResponsiveMediaCard
          key="hooksManagement"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().hooksManagement)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    if (checkAccess('tenantOptions')) {
      const config = getFeatureConfig('tenantOptions');
      cards.push(
        <ResponsiveMediaCard
          key="tenantOptions"
          title={config.title}
          text={config.description}
          onClick={() => goToPage(RouteUtils.routes().tenantOptions)}
          img={config.icon}
          allowed={true}
        />,
      );
    }

    return cards;
  };

  return (
    <GenericDirectory
      title="Clinic Settings"
      className="clinics-directory relative"
      topRowContent={
        <>
          <span className="large-title">
            <span className="clickable" onClick={() => goToPage(RouteUtils.routes().clinics)}>
              Clinic Settings
            </span>
            {!!tab && (
              <>
                &nbsp;{'>'} <span>{tab}</span>
              </>
            )}
          </span>
          {newPatientModal && (
            <DefaultModal
              className="modal staff-register-patient-modal"
              title="Register Patient"
              wrap={true}
              padding={true}
              content={<StaffRegisterPatient onSuccess={onRegisterSuccess} />}
              isOpen={newPatientModal}
              onRequestClose={() => setNewPatientModal(false)}
            />
          )}
          <div className="new-clinic-button">
            {addConfirmationDialog && (
              <ConfirmationDialog
                title="Add New Clinic"
                message="This will add a new clinic. Are you sure you want to proceed?"
                open={addConfirmationDialog}
                onConfirm={addClinic}
                onCancel={() => setAddConfirmationDialog(false)}
                loadingState={loadingAdd}
              />
            )}
            <NavStack
              stack={Stacks.Master}
              routes={{
                '/clinics/clinic-details': {
                  element: (
                    <>
                      <DefaultButton
                        name="Add Clinic"
                        icon="hospital"
                        style={null}
                        onClick={() => setAddConfirmationDialog(true)}
                      />
                    </>
                  ),
                  authenticated: true,
                },
              }}
            />
          </div>
        </>
      }
    >
      <NavStack
        stack="Master"
        authenticateAll
        routes={{
          ...generateRoutes(),
          '*': {
            element: (
              <div className="box padded-box h-full overflow-auto w-full">
                <div className="clinic-card-list row justify-center">{renderFeatureCards()}</div>
              </div>
            ),
          },
        }}
      />
    </GenericDirectory>
  );
};

/**
 * @param {any} props
 */
export const ClinicBadge = ({ clinic, onClick, selected, children, extraInfo = null }) =>
  clinic ? (
    <div className={'clinic-badge-wrapper' + (selected ? ' is-selected' : '')}>
      <GenericBadge
        className="clinic-badge"
        title={clinic.name || 'Unnamed'}
        info={
          <>
            <span>{MiscUtils.getFullAddress(clinic.address, false, ['state', 'country'])}</span>
            {extraInfo}
          </>
        }
        onClick={onClick ? () => onClick(clinic) : undefined}
      >
        {children}
      </GenericBadge>
    </div>
  ) : null;

export const EditableClinicBadge = (props) => {
  const { clinic, onClick } = props;
  return (
    <div className="clinic-badge-wrapper blue-icon clickable" onClick={onClick}>
      <ClinicBadge {...props} key={clinic?.id} />
      <Icon src="pencil" className="change" alt="Change Clinic" width="20px" />
    </div>
  );
};

export default ClinicsDirectory;
